// frontend/src/pages/Credit.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { fmtDate as fmtTz } from '../utils/time';

/* ---------------------- Small UI helpers ---------------------- */
function Alert({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid #fecaca',
        background: '#fff1f2',
        color: '#b91c1c',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>⚠ {message}</div>
        <button className="btn btn-sm secondary" onClick={onClose} aria-label="Dismiss">
          Dismiss
        </button>
      </div>
    </div>
  );
}

/* --------- Open API PDF safely; map backend errors to UX text --------- */
async function openPdfSafe(apiPath, setUiError) {
  try {
    const resp = await api.get(apiPath, { responseType: 'blob' });
    const blob =
      resp?.data instanceof Blob
        ? resp.data
        : new Blob([resp?.data ?? ''], { type: resp?.headers?.['content-type'] || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    let data = err?.response?.data;
    try {
      if (data instanceof Blob) {
        const txt = await data.text();
        try { data = JSON.parse(txt); } catch { data = { error: txt }; }
      }
    } catch {}
    const error = data?.error || 'Unable to open document.';
    const missing = Array.isArray(data?.missing) ? data.missing[0] : undefined;

    let msg = 'Unable to open document.';
    if (error === 'Forbidden' && missing) {
      if (missing === 'credit:export') msg = 'You don’t have permission to view or export credit notes.';
      else if (missing === 'invoices:export') msg = 'You don’t have permission to view or export invoices.';
      else msg = 'You don’t have permission to view this document.';
    } else if (typeof error === 'string' && error.toLowerCase().includes('no returns')) {
      msg = 'No return entries were found for this invoice.';
    } else if (typeof error === 'string') {
      msg = error;
    }
    setUiError(msg);
  }
}

export default function Credit() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');

  // HTML date inputs produce "YYYY-MM-DD"
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // UX error for PDF attempts
  const [uiError, setUiError] = useState('');

  // pagination (client-side)
  const [page, setPage] = useState(1); // 1-based
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = Number(localStorage.getItem('credit.rowsPerPage'));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });

  useEffect(() => { load(); }, []);

  // Debounce search/sort/date changes
  useEffect(() => {
    const t = setTimeout(() => load(search, sort), 300);
    return () => clearTimeout(t);
  }, [search, sort, fromDate, toDate]);

  // reset to first page when filters change
  useEffect(() => { setPage(1); }, [search, sort, fromDate, toDate]);

  async function load(q = '', srt = 'date_desc') {
    const params = new URLSearchParams({ search: q, sort: srt });

    // Backend expects plain dates; it converts to local TZ internally
    if (fromDate) params.set('from', fromDate); // "YYYY-MM-DD"
    if (toDate)   params.set('to', toDate);

    // Fetch all rows so client-side pager can slice
    params.set('limit', 'all');

    // ✅ correct resource (credit-notes)
    const resp = await api.get(`/credit-notes?${params.toString()}`);
    setList(resp.data || []);
  }

  const fmtMoney = (n) => Number(n || 0).toFixed(2);

  // pagination derived values
  const total = list?.length || 0;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / rowsPerPage)),
    [total, rowsPerPage]
  );

  // ensure current page is in bounds if data shrinks
  useEffect(() => {
    const maxPage = pageCount;
    if (page > maxPage) setPage(maxPage);
  }, [page, pageCount]);

  const onChangeRowsPerPage = (e) => {
    const v = Number(e.target.value || 25);
    localStorage.setItem('credit.rowsPerPage', String(v));
    setRowsPerPage(v);
    setPage(1);
  };

  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const pageRows = useMemo(() => list.slice(startIdx, endIdx), [list, startIdx, endIdx]);

  const gotoFirst = () => setPage(1);
  const gotoPrev  = () => setPage(p => Math.max(1, p - 1));
  const gotoNext  = () => setPage(p => Math.min(pageCount, p + 1));
  const gotoLast  = () => setPage(pageCount);

  // UX: prevent selecting an end date before start date
  const toMin = fromDate || undefined;

  return (
    <div className='container'>
      {/* Friendly error banner for any failed PDF attempts */}
      <Alert message={uiError} onClose={() => setUiError('')} />

      <div className='toolbar' style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <h2 style={{ marginRight:'auto' }}>Credit</h2>

        <input
          type="text"
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder='Search return, invoice, customer, phone'
          style={{
            padding: 10,
            boxSizing: 'border-box',
            flex: '1 1 600px',
            minWidth: 360
          }}
        />

        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          <label style={{ fontSize:12 }}>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={e=>setFromDate(e.target.value)}
            max={toDate || undefined}
          />
          <label style={{ fontSize:12 }}>To</label>
          <input
            type="date"
            value={toDate}
            onChange={e=>setToDate(e.target.value)}
            min={toMin}
          />
          {(fromDate || toDate) && (
            <button className="btn" onClick={()=>{ setFromDate(''); setToDate(''); }}>
              Clear
            </button>
          )}
        </div>

        <select value={sort} onChange={e=>setSort(e.target.value)} style={{ padding:8, flexShrink:0 }}>
          <option value='date_desc'>Date: Newest → Oldest</option>
          <option value='date_asc'>Date: Oldest → Newest</option>
          <option value='customer_asc'>Customer: A → Z</option>
          <option value='customer_desc'>Customer: Z → A</option>
          <option value='amount_desc'>Total: High → Low</option>
          <option value='amount_asc'>Total: Low → High</option>
        </select>
      </div>

      <div className='card' style={{ overflowX:'auto', marginTop: '12px' }}>
        <table className='table'>
          <thead>
            <tr>
              <th>Return</th>
              <th>Date</th>
              <th>Invoice</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Total Credit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(r => (
              <tr key={r.id || r.return_no}>
                <td>{r.return_no}</td>
                <td>{fmtTz(r.return_date || r.created_at)}</td>
                <td>{r.original_invoice_no || '-'}</td>
                <td>{r.customer_name || '-'}</td>
                <td>{r.customer_phone || '-'}</td>
                <td>{fmtMoney(Math.abs(r.total))}</td>
                <td>
                  {r.original_invoice_no ? (
                    <button
                      className='btn btn-sm'
                      onClick={() =>
                        openPdfSafe(
                          `/credit-notes/by-invoice/${encodeURIComponent(r.original_invoice_no)}/pdf`,
                          setUiError
                        )
                      }
                    >
                      Download
                    </button>
                  ) : '-'}
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={7} style={{ textAlign:'center', padding:24 }}>No credit notes</td></tr>
            )}
          </tbody>
        </table>

        {/* Pager */}
        <div className="flex" style={{
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderTop: '1px solid var(--border, #e5e7eb)',
          flexWrap: 'wrap'
        }}>
          <div className="flex" style={{ alignItems:'center', gap:8 }}>
            <label className="muted" htmlFor="rows-per-page">Rows/page</label>
            <select
              id="rows-per-page"
              className="input"
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              style={{ width: 96 }}
              aria-label="Rows per page"
            >
              {[10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="muted" style={{ marginLeft: 12 }}>
              {total
                ? `${startIdx + 1}–${Math.min(endIdx, total)} of ${total}`
                : '0 of 0'}
            </span>
          </div>

          <div className="flex" style={{ alignItems:'center', gap:6, flexWrap: 'wrap' }}>
            <button className="btn btn-sm secondary"
                    onClick={gotoFirst}
                    disabled={page <= 1}
                    aria-label="First page">«</button>
            <button className="btn btn-sm secondary"
                    onClick={gotoPrev}
                    disabled={page <= 1}
                    aria-label="Previous page">‹</button>
            <span className="muted" style={{ padding: '0 6px' }}>
              Page <b>{page}</b> / {pageCount}
            </span>
            <button className="btn btn-sm secondary"
                    onClick={gotoNext}
                    disabled={page >= pageCount}
                    aria-label="Next page">›</button>
            <button className="btn btn-sm secondary"
                    onClick={gotoLast}
                    disabled={page >= pageCount}
                    aria-label="Last page">»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
